import React, { useMemo, useState } from "react";
import {
  Stack,
  Group,
  TextInput,
  Select,
  Button,
  Text,
  Paper,
  ActionIcon,
  SimpleGrid,
} from "@mantine/core";
import { IconPlus, IconTrash, IconInfoCircle } from "@tabler/icons";
import { CONDITION_TYPES } from "../../data/mcaEntities";
import McaWatchFieldExplanationModal from "./McaWatchFieldExplanationModal";
import {
  buildWatchFieldSelectOptions,
  getWatchFieldSelectOption,
} from "./mcaWatchFieldLabels";

const emptyRow = () => ({ field: "", conditionType: "", conditionValue: "" });

const parseCondition = (row) => {
  if (!row.conditionType || row.conditionValue === "") return undefined;
  if (row.conditionType === "$in") {
    return { $in: row.conditionValue.split(",").map((s) => s.trim()) };
  }
  const num = Number(row.conditionValue);
  if (["$gt", "$gte", "$lt", "$lte"].includes(row.conditionType) && !Number.isNaN(num)) {
    return { [row.conditionType]: num };
  }
  return { [row.conditionType]: row.conditionValue };
};

const rowFromWatchField = (wf) => {
  if (!wf?.condition) return { field: wf?.field ?? "", conditionType: "", conditionValue: "" };
  const key = Object.keys(wf.condition)[0];
  const val = wf.condition[key];
  return {
    field: wf.field ?? "",
    conditionType: key,
    conditionValue: Array.isArray(val) ? val.join(", ") : String(val ?? ""),
  };
};

export const watchFieldsToRows = (watchFields = []) =>
  watchFields.length ? watchFields.map(rowFromWatchField) : [emptyRow()];

export const rowsToWatchFields = (rows) =>
  rows
    .filter((r) => r.field?.trim())
    .map((r) => {
      const condition = parseCondition(r);
      return condition ? { field: r.field.trim(), condition } : { field: r.field.trim() };
    });

const McaWatchFieldsEditor = ({
  rows,
  onChange,
  suggestedFields = [],
  entityKey = null,
}) => {
  const [explainOpen, setExplainOpen] = useState(false);
  const fieldPaths = useMemo(
    () => (Array.isArray(suggestedFields) ? suggestedFields.filter(Boolean) : []),
    [suggestedFields]
  );
  const fieldOptions = useMemo(
    () => buildWatchFieldSelectOptions(entityKey, fieldPaths),
    [entityKey, fieldPaths]
  );

  const updateRow = (idx, patch) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    onChange(next);
  };

  return (
    <Stack>
      <Text size="sm" color="dimmed">
        Leave empty to require approval on any update. Add rows to trigger only when specific fields change.
      </Text>
      {rows.map((row, idx) => (
        <Paper key={idx} p="md" withBorder>
          <SimpleGrid
            cols={4}
            spacing="sm"
            breakpoints={[{ maxWidth: "md", cols: 1 }]}
            sx={{ alignItems: "flex-end" }}
          >
            {fieldOptions.length > 0 ? (
              <Select
                label="Field"
                placeholder="Select field"
                data={[
                  ...fieldOptions,
                  ...(row.field &&
                  !fieldOptions.some((option) => option.value === row.field)
                    ? [getWatchFieldSelectOption(entityKey, row.field)].filter(Boolean)
                    : []),
                ]}
                value={row.field || null}
                onChange={(v) => updateRow(idx, { field: v ?? "" })}
                searchable
                clearable
                withinPortal
              />
            ) : (
              <TextInput
                label="Field"
                placeholder="e.g. status"
                value={row.field}
                onChange={(e) => updateRow(idx, { field: e.target.value })}
              />
            )}
            <Select
              label="Condition"
              data={CONDITION_TYPES}
              value={row.conditionType || null}
              onChange={(v) => updateRow(idx, { conditionType: v ?? "" })}
              clearable
              placeholder="Any change"
            />
            <TextInput
              label="Value"
              placeholder="e.g. INACTIVE or 100000"
              value={row.conditionValue}
              onChange={(e) => updateRow(idx, { conditionValue: e.target.value })}
              disabled={!row.conditionType}
            />
            <Group position="right" pb={4}>
              <ActionIcon
                variant="subtle"
                size="lg"
                onClick={() => onChange(rows.filter((_, i) => i !== idx))}
                disabled={rows.length === 1}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
          </SimpleGrid>
        </Paper>
      ))}
      <Group>
        <Button
          leftIcon={<IconPlus size={16} />}
          onClick={() => onChange([...rows, emptyRow()])}
        >
          Add watch field
        </Button>
        {entityKey && fieldPaths.length > 0 && (
          <Button
            variant="default"
            leftIcon={<IconInfoCircle size={16} />}
            onClick={() => setExplainOpen(true)}
          >
            Field guide
          </Button>
        )}
      </Group>
      <McaWatchFieldExplanationModal
        opened={explainOpen}
        onClose={() => setExplainOpen(false)}
        entityKey={entityKey}
        fieldPaths={fieldPaths}
      />
    </Stack>
  );
};

export default McaWatchFieldsEditor;
